import { DialogActions, DialogTitle } from "@material-ui/core";
import React, { Component } from "react";
import styled from "styled-components";

import PresentationLibraryController from "../../../controllers/PresentationLibraryController";
import AppController from "../../../core/AppController";
import { trackActivity } from "../../../core/utilities/utilities";
import { app } from "../../../namespaces";
import { BeautifulDialog, DialogContent } from "../../components/Dialogs/BaseDialog";
import GeneratingSlidesView from "../../views/AIGeneration/panes/GeneratePresentationPane/components/GeneratingSlidesView";
import { PromptPresentationInput, RefPromptPresentationInput } from "../../views/AIGeneration/panes/GeneratePresentationPane/components/PromptView";
import GeneratePresentationController, { GeneratePresentationState, PresentationGenerationStep } from "../../views/AIGeneration/panes/GeneratePresentationPane/controllers/GeneratePresentationController";
import { Breadcrumbs } from "../Breadcrumbs";
import Button from "../Button2";
import { FlexSpacer, Gap30, Gap5 } from "../Gap";
import { FlexBox } from "../LayoutGrid";
import { LoadingBot } from "../LoadingBot";
import { BlueButton } from "../UiComponents";

const SubTitle = styled.span`
    font-size: 18px;
    line-height: 18px;
    font-weight: 400;
`;

type Props = {
    onSuccess?: (email: string) => void;
    closeDialog: () => void;
} & GeneratePresentationState;

export const GeneratePresentationDialog = GeneratePresentationController.withState(class GeneratePresentationDialog extends Component<Props> {
    refInput = React.createRef<RefPromptPresentationInput>();

    async componentDidMount() {
        await GeneratePresentationController.reset(AppController.workspaceId, this.handleCreatePresentation);
    }

    handleCreatePresentation = async options => {
        const {
            closeDialog,
        } = this.props;

        const workspaceId = AppController.workspaceId;

        const createdPresentation = await PresentationLibraryController.createPresentation({
            workspaceId,
            name: options.name,
            theme: options.theme,
            slides: options.defaultSlides
        });
        await createdPresentation.prepareSlides();
        createdPresentation.slides.map(async slide => {
            await slide.getThumbnailUrl(createdPresentation.id, "small", 0, true);
        });

        trackActivity("Presentation", "Generate", null, null, {
            presentationId: createdPresentation.id,
            numSlides: options.defaultSlides.length,
            fromTour: true,
        });

        AppController.showEditor({ presentationId: createdPresentation.id, withAppcue: "1c65534f-fa6e-498c-b02a-fce139a94c85" });

        closeDialog();
    }

    skip() {
        const {
            closeDialog,
        } = this.props;

        trackActivity("GeneratePresentationTour", "Skipped", null, null, {
            object: "button",
            object_label: "Skip",
            action: "clicked",
            "experiment_id": "5FC18A79E182FE7C764425D4F852F501",
            experiment_group_assignment: "control"
        });

        closeDialog();
    }

    render() {
        const {
            closeDialog,
            startGenerationParams,
            step,
        } = this.props;

        let maxWidth: any = "md";
        if (
            step === PresentationGenerationStep.GENERATING_SLIDES ||
            step === PresentationGenerationStep.DONE
        ) {
            maxWidth = "lg";
        }

        const breadCrumbIndex = (
            step === PresentationGenerationStep.PROMPT ? 0
                : step === PresentationGenerationStep.GENERATING_OUTLINE ? 1
                    : 2
        );

        return (
            <BeautifulDialog
                preventClose
                closeDialog={closeDialog}
                closeButton={false}
                maxWidth={maxWidth}
                fullWidth={maxWidth !== "lg"}
                style={{ zIndex: 10000 }}
            >
                <Gap30 />
                <Breadcrumbs
                    activeIndex={breadCrumbIndex}
                    crumbs={[
                        {
                            content: <span>1</span>,
                            label: "Provide a prompt",
                        },
                        {
                            content: <span>2</span>,
                            label: "Drafting a story",
                        },
                        {
                            content: <span>3</span>,
                            label: "Generating slides",
                        },
                    ]}
                />
                {step === PresentationGenerationStep.PROMPT &&
                    <>
                        <DialogTitle>
                            <FlexBox column>
                                <span>Try generating a presentation with AI!</span>
                                <Gap5 />
                                <SubTitle></SubTitle>
                            </FlexBox>
                        </DialogTitle>
                        <DialogContent>
                            <PromptPresentationInput
                                ref={this.refInput}
                                startGenerationParams={startGenerationParams}
                                hideActionButton
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => this.skip()}
                                label="Skip"
                                color="secondary"
                                variant="text"
                            />
                            <FlexSpacer /> {/* @ts-ignore() */}
                            <BlueButton onClick={() => {
                                this.refInput.current.handleStartGeneration();
                                trackActivity("GeneratePresentationTour", "Confirm", null, null, {
                                    object: "button",
                                    object_label: "Generate Presentation",
                                    action: "clicked",
                                    experiment_id: "5FC18A79E182FE7C764425D4F852F501",
                                    experiment_group_assignment: "control"
                                });
                            }}>Generate Presentation</BlueButton>
                        </DialogActions>
                    </>
                }
                {step === PresentationGenerationStep.GENERATING_OUTLINE &&
                    <>
                        <DialogTitle>
                            <FlexBox column>
                                <span>Sit back and relax while we create a draft for you.</span>
                                <Gap5 />
                                <SubTitle>Once your draft is ready, we'll automatically advance you to the next step.</SubTitle>
                            </FlexBox>
                        </DialogTitle>
                        <DialogContent>
                            <LoadingBot
                                labels={[
                                    "Gathering data...",
                                    "Plotting narrative...",
                                    "Curating supporting visuals...",
                                ]}
                            />
                        </DialogContent>
                    </>
                }
                {(
                    step === PresentationGenerationStep.GENERATING_SLIDES ||
                    step === PresentationGenerationStep.DONE
                ) &&
                    <>
                        <DialogTitle>
                            <FlexBox column>
                                <span>Drumroll please... Your presentation is almost ready!</span>
                                <Gap5 />
                                <SubTitle>Update your presentation's theme and fine-tune its content once your slides are ready.</SubTitle>
                            </FlexBox>
                        </DialogTitle>
                        <DialogContent>
                            <GeneratingSlidesView
                                excludeStatusLabel
                            />
                        </DialogContent>
                    </>
                }
            </BeautifulDialog>
        );
    }
});
